import React, { CSSProperties, forwardRef, useMemo } from 'react';
import Select from 'react-select';
import type {
    ActionMeta,
    GroupBase,
    GroupHeadingProps,
    MenuPlacement,
    MenuPosition,
    OnChangeValue,
    StylesConfig,
    SelectComponentsConfig,
} from 'react-select';
import SelectBase from 'react-select/base';
import CreateSelect from 'react-select/creatable';

import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';

import StyledTextWithInfoTooltip from '../../display/text/StyledTextWithInfoTooltip';
// components
import InputError from '../../inputs/inputError';
// constants
import {
    GroupedOption,
    SearchableSelectType,
} from '../../inputs/searchableSelect';
import defaultCustomStyle from './select-style';

interface CreateSelectDropdownProps {
    id?: string;
    label?: string;
    infoDescription?: React.ReactNode | string;
    options: SearchableSelectType[] | GroupedOption[];
    name: string;
    onChange?: (
        field: string,
        value: SearchableSelectType | SearchableSelectType[],
        action?: ActionMeta<SearchableSelectType>,
    ) => void;
    onBlur?: (field: string, isTouched?: boolean) => void;
    value?: SearchableSelectType | SearchableSelectType[];
    placeholder?: string;
    ref?: any;
    isMultiple?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    isClearable?: boolean;
    error?: string;
    touched?: boolean;
    creatable?: boolean;
    wrapperStyle?: CSSProperties;
    menuPortalTarget?: HTMLElement | null;
    menuIsOpen?: boolean;
    onMenuClose?: () => void;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
    customStyle?: StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    components?: SelectComponentsConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    className?: string;
    footer?: React.ReactNode;
    hideSelectedOptions?: boolean;
    isRequired?: boolean;
    isSortOption?: boolean;
    isSearchable?: boolean;
    onMenuOpen?: () => void;
    closeMenuOnSelect?: boolean;
    onInputChange?: (newValue: string, actionMeta: any) => void;
    formatCreateLabel?: (inputValue: string) => React.ReactNode;
}

const AampeCreateSelectDropdown: React.FC<
    React.PropsWithChildren<CreateSelectDropdownProps>
> = forwardRef<
    SelectBase<SearchableSelectType, boolean, GroupBase<SearchableSelectType>>,
    CreateSelectDropdownProps
>(
    (
        {
            id,
            options,
            name,
            onChange,
            onBlur,
            value,
            placeholder,
            isMultiple,
            isDisabled,
            isClearable = true,
            error,
            touched,
            creatable,
            isLoading,
            label,
            wrapperStyle,
            menuPortalTarget,
            menuIsOpen,
            menuPlacement,
            components,
            customStyle,
            className,
            infoDescription,
            footer,
            hideSelectedOptions = true,
            isRequired,
            isSortOption = true,
            menuPosition,
            isSearchable = true,
            onMenuClose,
            onMenuOpen,
            closeMenuOnSelect,
            onInputChange,
            formatCreateLabel,
        },
        ref,
    ) => {
        const handleChange = (
            newValue: OnChangeValue<SearchableSelectType, boolean>,
            actionMeta: ActionMeta<SearchableSelectType>,
        ) => {
            if (!onChange) {
                return;
            }
            if (isMultiple && Array.isArray(newValue)) {
                onChange(name, newValue, actionMeta);
            } else {
                if (newValue) {
                    onChange(name, newValue as SearchableSelectType);
                } else {
                    onChange(name, { value: '', label: '' });
                }
            }
        };

        const handleBlur = () => {
            if (onBlur) {
                onBlur(name, true);
            }
        };

        const finalOptions = React.useMemo(() => {
            return isSortOption
                ? [...options]?.sort((a, b) => a?.label?.localeCompare(b.label))
                : options;
        }, [options, isSortOption]);

        const Component = creatable ? CreateSelect : Select;
        const customComponent = useMemo(() => {
            let customComponents: SelectComponentsConfig<
                SearchableSelectType,
                boolean,
                GroupBase<SearchableSelectType>
            > = {
                IndicatorSeparator: () => null,
                ...components,
            };

            const DividerOption = () => (
                <div
                    style={{
                        height: '1px',
                        backgroundColor: aampeTheme.colors.interactiveWG01,
                        margin: aampeTheme.spacing4(0, 3),
                        padding: '0 2px 0 2px',
                    }}
                />
            );

            const GroupHeading = (
                props: GroupHeadingProps<
                    SearchableSelectType,
                    boolean,
                    GroupBase<SearchableSelectType>
                >,
            ) => {
                const { data } = props;

                if (data.label === 'Highlighted Labels') {
                    return null;
                }

                return <DividerOption />;
            };

            if (footer) {
                customComponents = {
                    ...customComponents,
                    MenuList: (props) => (
                        <CustomMenuList data-name={`${name}-select-menu-list`}>
                            <OptionContainer>{props.children}</OptionContainer>
                            <Footer>{footer}</Footer>
                        </CustomMenuList>
                    ),
                    GroupHeading: GroupHeading,
                };
            }
            return customComponents;
        }, [footer, components, name]);

        return (
            <div style={wrapperStyle}>
                {label && (
                    <StyledTextWithInfoTooltip
                        text={label}
                        description={infoDescription}
                        isRequired={isRequired}
                    />
                )}
                <Component
                    id={id}
                    className={className}
                    ref={ref}
                    onMenuOpen={onMenuOpen}
                    isMulti={isMultiple}
                    isClearable={isClearable}
                    // Reason: https://github.com/JedWatson/react-select/issues/3066
                    value={value ?? ('' as any)}
                    options={finalOptions}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    styles={customStyle ?? defaultCustomStyle}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    components={customComponent}
                    onMenuClose={onMenuClose}
                    menuIsOpen={menuIsOpen}
                    closeMenuOnSelect={closeMenuOnSelect}
                    menuPlacement={menuPlacement}
                    menuPortalTarget={menuPortalTarget}
                    hideSelectedOptions={hideSelectedOptions}
                    menuPosition={menuPosition}
                    isSearchable={isSearchable}
                    onInputChange={onInputChange}
                    formatCreateLabel={formatCreateLabel}
                />
                {error && touched && <InputError error={error} />}
            </div>
        );
    },
);

export default AampeCreateSelectDropdown;
const CustomMenuList = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '275px',
});
const OptionContainer = styled.div({
    overflowY: 'auto',
    maxHeight: '275px',
});
const Footer = styled.div({
    position: 'sticky',
    bottom: 0, // Sticks the footer to the bottom
    borderTop: '1px solid #ccc',
});
